import React, { useState } from 'react'
import {
    Box,
    Container,
    Flex,
    SimpleGrid,
    Button,
    HStack, VStack,
    Text,
    Icon,
    useDisclosure,
    Heading,
    IconButton,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerOverlay,
    DrawerContent,
    DrawerCloseButton,
    useBreakpointValue,
    Center,
    Input,
    InputGroup,
    InputLeftElement,
    Modal,
    ModalOverlay,
    ModalContent,
    ModalCloseButton
  } from '@chakra-ui/react';
import { AddIcon, HamburgerIcon, ArrowBackIcon, StarIcon, SearchIcon } from '@chakra-ui/icons';
import { ModernSidebar, GoalCard, UserMenu, BreadcrumbNav, useMobileBottomPadding, MobileBottomNav, UserProfileModal } from './SharedComponents';

// Import the SearchResults component from Dashboard
const SearchResults = ({ results, query, onContinueJourney, onContinueChapter, onRouteChange }) => {
  const hasResults = results && (results.goals.length > 0 || results.exercises.length > 0)

  return (
    <Box>
      <Heading size="lg" color="gray.800" mb={6}>
        Search Results for "{query}"
      </Heading>
      
      {!hasResults && (
        <Center py={12}>
          <VStack spacing={4}>
            <Icon as={SearchIcon} boxSize={12} color="gray.400" />
            <Text fontSize="lg" color="gray.600">No results found for "{query}"</Text>
            <Text color="gray.500">Try searching with different keywords</Text>
          </VStack>
        </Center>
      )}

      {/* Goals Results */}
      {results.goals.length > 0 && (
        <Box mb={8}>
          <Heading size="md" color="gray.700" mb={4}>Goals ({results.goals.length})</Heading>
          <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={4}>
            {results.goals.map((goal) => (
              <GoalCard 
                key={goal.id}
                goal={goal}
                onContinueJourney={onContinueJourney}
                showDeleteButton={false}
              />
            ))}
          </SimpleGrid>
        </Box>
      )}

      {/* Exercises Results */}
      {results.exercises.length > 0 && (
        <Box>
          <Heading size="md" color="gray.700" mb={4}>Exercises ({results.exercises.length})</Heading>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {results.exercises.map((exercise) => (
              <Box
                key={exercise.id}
                bg="white"
                shadow="md"
                rounded="lg"
                p={6}
                cursor="pointer"
                _hover={{ shadow: 'lg' }}
                onClick={() => onContinueChapter(exercise.id)}
              >
                <VStack align="start" spacing={3}>
                  <HStack justify="space-between" w="full">
                    <Heading size="sm" color="purple.700">{exercise.name}</Heading>
                    {exercise.completed && <Icon as={StarIcon} color="green.500" />}
                  </HStack>
                  <Text fontSize="sm" color="gray.600" noOfLines={2}>
                    {exercise.description}
                  </Text>
                  <HStack>
                    <Text fontSize="xs" bg="purple.100" color="purple.700" px={2} py={1} rounded="md">
                      {exercise.goal_name}
                    </Text>
                    <Text fontSize="xs" color="gray.500">Part {exercise.part || 1}</Text>
                  </HStack>
                </VStack>
              </Box>
            ))}
          </SimpleGrid>
        </Box>
      )}
    </Box>
  )
}

const AllGoalsView = ({ state, onRouteChange, getOverview, getUserStats, onSearch, onContinueJourney, onDeleteJourney, onRandomChapter, toggleSidebar, setLoading }) => {
  const { isOpen, onOpen, onClose } = useDisclosure()
  const { isOpen: isSearchOpen, onOpen: onSearchOpen, onClose: onSearchClose } = useDisclosure()
  const { isOpen: isProfileOpen, onOpen: onProfileOpen, onClose: onProfileClose } = useDisclosure()
  const isMobile = useBreakpointValue({ base: true, lg: false })
  const [searchQuery, setSearchQuery] = useState('')
  const bottomPadding = useMobileBottomPadding()
  
  React.useEffect(() => {
    // Always refresh the overview when AllGoalsView loads (without global loading)
    getOverview(false)
    // Get user stats (without global loading to avoid loading overload)
    getUserStats && getUserStats(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const handleSearch = (e) => {
    const query = e.target.value
    setSearchQuery(query)
    if (onSearch) {
      onSearch(query, 'all', false) // false = no global loading for real-time search
    }
  }
  
  return (
    <Box minH="100vh" bg="gray.50">
      {/* Mobile Navigation */}
      {isMobile && (
        <Drawer isOpen={isOpen} placement="left" onClose={onClose}>
          <DrawerOverlay />
          <DrawerContent bg="white">
            <DrawerCloseButton color="gray.700" />
            <DrawerHeader color="brand.600" fontSize="xl" fontWeight="bold">
              Grow
            </DrawerHeader>
            <DrawerBody>
              <ModernSidebar
                state={state}
                onRouteChange={onRouteChange} 
                getOverview={getOverview}
                onRandomChapter={onRandomChapter}
                toggleSidebar={toggleSidebar}
                onClose={onClose}
              />
            </DrawerBody>
          </DrawerContent>
        </Drawer>
      )}
      
      <Flex>
        {/* Desktop Sidebar */}
        {!isMobile && !state.sidebarCollapsed && (
          <Box 
            w="280px" 
            bg="white" 
            minH="100vh" 
            position="fixed" 
            left={0} 
            top={0}
            zIndex={10}
            borderRight="1px"
            borderColor="gray.200"
            boxShadow="sm"
            transition="all 0.3s ease"
          >
            <ModernSidebar
              state={state}
              onRouteChange={onRouteChange}
              getOverview={getOverview}
              onRandomChapter={onRandomChapter}
              toggleSidebar={toggleSidebar}
            />
          </Box>
        )}
        
        {/* Main Content */}
        <Box flex={1} ml={isMobile ? 0 : state.sidebarCollapsed ? 0 : "280px"} transition="margin-left 0.3s ease" minW={0} overflow="hidden">
          {/* Top Navigation */}
          <Box bg="white" borderBottom="1px" borderColor="gray.200" px={{ base: 4, md: 6 }} py={{ base: 3, md: 3 }}>
            <Flex align="center" justify="space-between">
              <HStack spacing={3}>
                {isMobile && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={onOpen}
                    variant="ghost"
                    colorScheme="brand"
                    size="sm"
                  />
                )}
                {!isMobile && state.sidebarCollapsed && (
                  <IconButton
                    icon={<HamburgerIcon />}
                    onClick={toggleSidebar}
                    variant="ghost"
                    color="brand.600"
                    size="sm"
                    _hover={{
                      color: 'brand.700',
                      bg: 'brand.50'
                    }}
                  />
                )}
                {!isMobile && (
                  <>
                    <IconButton
                      icon={<ArrowBackIcon />}
                      variant="ghost"
                      colorScheme="brand"
                      onClick={() => onRouteChange('dashboard')}
                      size="sm"
                      aria-label="Back to Dashboard"
                    />
                    <VStack align="start" spacing={0}>
                      <Heading size="md" color="gray.800">
                        All Goals
                      </Heading>
                      <Text fontSize="sm" color="gray.600">
                        Your journey goals
                      </Text>
                    </VStack>
                  </>
                )}
              </HStack>

              <HStack spacing={4}>
                {!isMobile && (
                  <>
                    <InputGroup maxW="300px">
                      <InputLeftElement pointerEvents='none'>
                        <SearchIcon color='gray.400' />
                      </InputLeftElement>
                      <Input
                        placeholder="Search goals & exercises..."
                        value={searchQuery}
                        onChange={handleSearch}
                        bg="white"
                        borderColor="gray.300"
                        _hover={{ borderColor: 'brand.300' }}
                        _focus={{ borderColor: 'brand.500', boxShadow: '0 0 0 1px var(--chakra-colors-brand-500)' }}
                      />
                    </InputGroup>
                    <Button
                      leftIcon={<AddIcon />}
                      variant="outline"
                      colorScheme="purple"
                      size="sm"
                      onClick={() => onRouteChange('createGoal')}
                    >
                      New Goal
                    </Button>
                  </>
                )}
                <UserMenu onRouteChange={onRouteChange} username={state.username} userStats={state.userStats} />
              </HStack>
            </Flex>
          </Box>
          
          {/* Breadcrumb Navigation */}
          <BreadcrumbNav 
            route={state.route} 
            onRouteChange={onRouteChange} 
          />
          
          {/* Content */}
          <Container maxW="7xl" py={{ base: 4, md: 6 }} px={{ base: 4, md: 6 }} pb={bottomPadding}>
            {/* Search Results */}
            {state.searchResults && searchQuery && (
              <SearchResults 
                results={state.searchResults}
                query={state.searchQuery}
                onContinueJourney={onContinueJourney}
                onContinueChapter={() => {}} // Will be handled by SearchResults component
                onRouteChange={onRouteChange}
              />
            )}
            
            {/* Normal Goals Grid */}
            {(!state.searchResults || !searchQuery) && (
              <AllGoals
                state={state}
                onContinueJourney={onContinueJourney}
                onDeleteJourney={onDeleteJourney}
                setLoading={setLoading}
                onRouteChange={onRouteChange}
              />
            )}
          </Container>
        </Box>
      </Flex>

      {/* Mobile Search Modal */}
      <Modal isOpen={isSearchOpen} onClose={onSearchClose} size="full">
        <ModalOverlay />
        <ModalContent bg="gray.50" p={0}>
          <ModalCloseButton zIndex={3} />
          <Box p={6}>
            <VStack spacing={4} align="stretch">
              <Heading size="md" color="gray.800" mb={2}>Search</Heading>
              <InputGroup>
                <InputLeftElement pointerEvents='none'>
                  <SearchIcon color='gray.400' />
                </InputLeftElement>
                <Input
                  placeholder="Search goals & exercises..."
                  value={searchQuery}
                  onChange={handleSearch}
                  bg="white"
                  size="lg"
                  autoFocus
                />
              </InputGroup>
              
              {/* Search Results */}
              {state.searchResults && (
                <SearchResults 
                  results={state.searchResults}
                  query={state.searchQuery}
                  onContinueJourney={(id) => {
                    onContinueJourney(id)
                    onSearchClose()
                  }}
                  onContinueChapter={(id) => {
                    // Handle exercise click - navigate to practice view
                    onSearchClose()
                  }}
                  onRouteChange={(route, params) => {
                    onRouteChange(route, params)
                    onSearchClose()
                  }}
                />
              )}
            </VStack>
          </Box>
        </ModalContent>
      </Modal>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav
        currentRoute="allGoalsView"
        onRouteChange={onRouteChange}
        onRandomChapter={onRandomChapter}
        onSearchOpen={onSearchOpen}
        onProfileOpen={onProfileOpen}
      />

      {/* Profile Modal for Mobile */}
      <UserProfileModal
        isOpen={isProfileOpen}
        onClose={onProfileClose}
        username={state.username}
        userStats={state.userStats}
      />
    </Box>
  )
}

const AllGoals = ({ state, onContinueJourney, onDeleteJourney, setLoading, onRouteChange }) => {
  const overview = state.overview
  
  if (!overview.exists || !overview.items || overview.items.length === 0) {
    return (
      <Center py={20}>
        <VStack spacing={6}>
          <Box textAlign="center">
            <Icon as={StarIcon} boxSize={12} color="gray.400" mb={4} />
            <Heading size="lg" color="gray.600" mb={2}>
              No goals yet
            </Heading>
            <Text color="gray.500" fontSize="lg">
              Create your first goal to begin your journey
            </Text>
          </Box>
          <Button
            leftIcon={<AddIcon />}
            colorScheme="purple"
            size="lg"
            onClick={() => onRouteChange('createGoal')}
          >
            Create First Goal
          </Button>
        </VStack>
      </Center>
    )
  }

  return (
    <VStack spacing={{ base: 4, md: 6 }} align="stretch">
      <HStack justify="space-between" flexWrap="wrap" gap={2}>
        <Heading size={{ base: 'sm', md: 'md' }} color="gray.700">Your Goals</Heading>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="gray.500">
          Click on a goal to view and continue your practices
        </Text>
      </HStack>

      <SimpleGrid columns={{ base: 1, md: 2, xl: 3 }} spacing={{ base: 3, md: 5 }}>
        {overview.items.map((goal) => (
          <GoalCard
            key={goal.id}
            goal={goal}
            onContinueJourney={onContinueJourney}
            setLoading={setLoading}
            showDeleteButton={true}
            onDeleteJourney={onDeleteJourney}
          />
        ))}
      </SimpleGrid>
    </VStack>
  )
}

// GoalCard component moved to SharedComponents.js

// ModernSidebar component moved to SharedComponents.js

// UserMenu component moved to SharedComponents.js

export default AllGoalsView